// Pure formatters + design tokens used by every dashboard card.
// Lives alongside real.tsx so it's obvious which page consumes it.
import { ClipboardTextIcon, CoinsIcon, PackageIcon, ShoppingCartIcon } from "@phosphor-icons/react"
import { i18n } from "@dehub/theme"

// Fallback only. /ord/dashboard/main already sends a colour per status row
// (ORDER_STATUS[...].color), so OrderStatusCard reads that first; this map is
// what keeps a row from going grey if an older backend omits it. Keys are the
// names the backend actually sends — sentName / receivedName, Title-case. The
// previous ALL-CAPS keys never matched, so every status rendered grey.
export const STATUS_COLOR: Record<string, string> = {
  "Түр төлөв"            : "var(--mantine-color-gray-4)",
  "Бүртгэсэн"            : "var(--mantine-color-blue-5)",
  "Хяналтаас татгалзсан" : "var(--mantine-color-gray-7)",
  "Хянаад илгээсэн"      : "var(--mantine-color-indigo-7)",
  "Ирсэн"                : "var(--mantine-color-indigo-7)",
  "Цуцалсан"             : "var(--mantine-color-pink-6)",
  "Баталсан"             : "var(--mantine-color-green-6)",
  "Баталгаажсан"         : "var(--mantine-color-green-7)",
  "Татгалзсан"           : "var(--mantine-color-red-7)",
  "Авто цуцлагдсан"      : "var(--mantine-color-violet-7)",
  "Бэлтгэж байна"        : "var(--mantine-color-lime-5)",
  "Хүргэж байна"         : "var(--mantine-color-yellow-5)",
  "Хүргэсэн"             : "var(--mantine-color-teal-6)",
  "Дууссан"              : "var(--mantine-color-green-7)",
}

export interface StatConfig {
  icon: typeof ShoppingCartIcon
  bg: string
  stroke: string
  isCurrency: boolean
  labelKey: string
  /** Used instead of labelKey on the BUYER portal, where the same figure is
   *  the business's spend, not its sales. */
  buyerLabelKey?: string
}

export const STAT_CONFIGS: StatConfig[] = [
  { icon: ShoppingCartIcon,  bg: "var(--mantine-color-red-0)",    stroke: "var(--mantine-color-red-6)",    isCurrency: true,  labelKey: "stats.salesOrder", buyerLabelKey: "stats.purchaseOrder" },
  { icon: PackageIcon,       bg: "var(--mantine-color-red-0)",    stroke: "var(--mantine-color-red-6)",    isCurrency: false, labelKey: "stats.orderCount" },
  { icon: ClipboardTextIcon, bg: "var(--mantine-color-orange-0)", stroke: "var(--mantine-color-orange-5)", isCurrency: false, labelKey: "stats.orderItems" },
  { icon: CoinsIcon,         bg: "var(--mantine-color-indigo-0)", stroke: "var(--mantine-color-indigo-6)", isCurrency: true,  labelKey: "stats.averageOrder" },
]

export function formatCount(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}${i18n.t("dashboard:numberFormat.million")}`
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}${i18n.t("dashboard:numberFormat.thousand")}`
  return value.toLocaleString()
}

export function formatCurrencyShort(value: number): { num: string; unit: string } {
  if (value >= 1_000_000) return { num: (value / 1_000_000).toFixed(2), unit: `${i18n.t("dashboard:numberFormat.million")} ₮` }
  if (value >= 1_000) return { num: (value / 1_000).toFixed(0), unit: `${i18n.t("dashboard:numberFormat.thousand")} ₮` }
  return { num: value.toLocaleString(), unit: "₮" }
}

export interface FormattedChange {
  label: string
  isPositive: boolean
  isNew: boolean
  /** The response carried no percentage at all — an older backend. Better an
   *  em dash than a number the card would be making up. */
  isUnknown: boolean
}

// Takes the month-over-month percentage, NOT the absolute delta. Reading the
// delta here is what printed a 4.5M₮ rise as "4500000.00%", and then — once a
// sanity cap was bolted on — as "Шинэ" for every KPI that moved at all.
export function formatChange(changePct: number | null | undefined): FormattedChange {
  if (changePct === undefined) {
    return { label: "-", isPositive: true, isNew: false, isUnknown: true }
  }
  if (changePct === null || !isFinite(changePct)) {
    return { label: i18n.t("dashboard:numberFormat.new"), isPositive: true, isNew: true, isUnknown: false }
  }
  return {
    label     : `${Math.abs(changePct).toFixed(1)}%`,
    isPositive: changePct >= 0,
    isNew     : false,
    isUnknown : false,
  }
}

export type RecentOrderStatusVariant = "confirmed" | "delivering" | "delivered" | "preparing"

// Matched against the names /ord/dashboard/main sends (ORDER_STATUS sentName /
// receivedName). These are Title-case; comparing them to ALL-CAPS literals, as
// this used to, sent every row down the "preparing" branch.
export function statusVariant(status: string | undefined | null): RecentOrderStatusVariant {
  if (status === "Баталгаажсан" || status === "Баталсан") return "confirmed"
  if (status === "Хүргэж байна") return "delivering"
  if (status === "Хүргэсэн" || status === "Дууссан") return "delivered"
  return "preparing"
}
